// Modified: 2026-01-26
/**
 * Main Scenario Runner for SAFECARE Policy Simulator
 * Orchestrates all calculators and returns comprehensive results
 */

import { BaselineData } from '../data/baseline.js';
import { calculateRevenueProjection, calculateYearRevenue } from './revenueCalculator.js';
import { calculateSpendingProjection, calculateYearSpending } from './spendingCalculatorV2.js'; // V2: Medicare-based, bill-compliant
import { calculateTrustFundProjection, calculateDebtProjection, calculateFederalDebtImpact } from './fiscalCalculator.js';
import { calculateHouseholdBurdenByDecile, calculateAggregateHouseholdSavings } from './householdCalculator.js';
import { calculateDebtProjections, calculateWorkforceDebtImpact } from './debtCalculator.js';
import { calculateSuccessGauge } from './successGaugeCalculator.js';
import { calculateMacroImpacts, calculateAnnualGrowthBoost } from './macroCalculator.js';
import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';
import { applyEventsToConfig, getEventMultipliers } from './events.js'; // Added for dynamic events
import { getGeneralFundLimitForYearsActive } from './fiscalCalculatorHelpers.js';
import { isPipelineValidForYear, getPipelineEntryForYear, applyPipelineSpending, getPipelineLtssFields } from './pipelineSpendingAdapter.js';

/**
 * Run a complete SAFECARE scenario with the given policy configuration
 * Returns comprehensive results for visualization
 * @param {Object} baseConfig - The policy configuration object
 * @param {Array} enabledEventIds - List of active dynamic event IDs (optional)
 */
/**
 * Run a complete SAFECARE scenario with the given policy configuration
 * Returns comprehensive results for visualization, comparing:
 * 1. Safecare Control (No Events)
 * 2. Safecare Scenario (With Events)
 *
 * @param {Object} baseConfig - The policy configuration object
 * @param {Array|Object} enabledEventIds - List of active dynamic event IDs (optional)
 */
/**
 * Run a complete SAFECARE scenario with the given policy configuration
 * Returns comprehensive results for visualization, comparing:
 * 1. Safecare Control (No Events)
 * 2. Safecare Scenario (With Events)
 *
 * @param {Object} baseConfig - The policy configuration object
 * @param {Array|Object} enabledEventIds - List of active dynamic event IDs (optional)
 * @param {Function} structuralOverrides - Optional function to modify compounding logic context (Level 3 Overrides)
 */
export function runScenario(baseConfig, enabledEventIds = [], structuralOverrides = null, projectionTimeline = null) {
    // ------------------------------------------------------------------------
    // PASS 1: SAFECARE CONTROL (BASELINE)
    // Run the simulation with NO events active to establish the "Clean" baseline
    // ------------------------------------------------------------------------
    // Note: We intentionally pass empty events to get the pure policy impact
    // We also pass null for structuralOverrides for the control? 
    // debatable. If the scenario is "Fundamental Structural Change", maybe the baseline should also reference it?
    // No, usually we want to see the delta from "Standard Safecare".
    // "The Great Stagnation" compares against "Standard Safecare" (or Statutory Default).
    // So Control = Standard, Scenario = Stagnation.
    const controlResults = runSimulationPass(baseConfig, {}, null, projectionTimeline);

    // ------------------------------------------------------------------------
    // PASS 2: SAFECARE SCENARIO (TREATMENT)
    // Run the simulation WITH events active to see the impact of shocks
    // ------------------------------------------------------------------------
    const scenarioResults = runSimulationPass(baseConfig, enabledEventIds, structuralOverrides, projectionTimeline);

    // ------------------------------------------------------------------------
    // RETURN CONSOLIDATED RESULTS
    // ------------------------------------------------------------------------
    return {
        // Core Data
        baselineResults: controlResults.yearlyResults,    // Blue Line: "Safecare (No Events)"
        scenarioResults: scenarioResults.yearlyResults,   // Purple Line: "Safecare (With Events)"

        // Pass-through metadata (usually from scenario run)
        config: scenarioResults.config,
        summary: scenarioResults.summary,
        householdBurden: scenarioResults.householdBurden,
        householdSummary: scenarioResults.householdSummary,
        workforceImpact: scenarioResults.workforceImpact,
        macroImpacts: scenarioResults.macroImpacts,
        successGauge: scenarioResults.successGauge,
        rateAdjustmentHistory: scenarioResults.rateAdjustmentHistory,
        activeEventsLog: scenarioResults.activeEventsLog,
        trustFund: scenarioResults.trustFund,

        // Errors handled gracefully
        error: scenarioResults.error || controlResults.error,

        // Validation Metadata
        statutoryInputsUsed: [
            'SAFECARE_BILL_PARAMETERS.revenue.employerRate',
            'SAFECARE_BILL_PARAMETERS.revenue.individualBaseRate',
            'SAFECARE_BILL_PARAMETERS.revenue.selfEmployedBaseRate',
            'SAFECARE_BILL_PARAMETERS.costSharing.annualCopayCapPerPerson',
            'SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMin',
            'SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMax',
            'SAFECARE_BILL_PARAMETERS.trustFund.generalFundLimits',
            'SAFECARE_BILL_PARAMETERS.drugPricing.irpMultiplier'
        ],
        assumptionInputsUsed: [
            'taxEvasionRate',
            'payrollBaseErosion',
            'surtaxAvoidance',
            'providerParticipationRate',
            'utilizationSurge',
            'drugInnovationReduction',
            'medicalTourismLeakage'
        ]
    };
}

/**
 * Internal Simulation Pass - The Core Loop
 * Runs one complete projection based on inputs
 * @param {Object} projectionTimeline - Optional projection_timeline.json from map-hcs pipeline
 */
function runSimulationPass(baseConfig, enabledEventIds = [], structuralOverrides = null, projectionTimeline = null) {
    // ========================================================================
    // 0. VALIDATE AND NORMALIZE CONFIG
    // ========================================================================
    // Clone config to avoid mutation side effects
    const config = { ...baseConfig };

    // Ensure startYear is set
    if (!config.startYear) {
        config.startYear = 2027;
    }

    // Ensure macroFeedbackSensitivity is set
    if (config.macroFeedbackSensitivity === undefined) {
        config.macroFeedbackSensitivity = 1.0;  // Full sensitivity
    }

    // Validate enforceTrustFundRules
    if (config.enforceTrustFundRules === undefined) {
        config.enforceTrustFundRules = 1;  // Full enforcement
    }

    // Debt projection realism: only a fraction of federal health savings plausibly flow to debt paydown
    // (Congress may spend savings elsewhere; 100% debt paydown is unrealistic)
    const debtReductionReflow = config.debtReductionReflow ?? 0.25;

    // Config Validator (Simplified)
    if (config.employerRate < 0) config.employerRate = 0;

    try {
        // Filter baseline to start from config.startYear
        const startIndex = BaselineData.nheBaseline.findIndex(d => d.year === config.startYear);
        if (startIndex === -1) {
            throw new Error(`Start year ${config.startYear} not found in baseline data`);
        }

        // ========================================================================
        // AUTOMATIC RATE ADJUSTMENTS (Sec 206) & DYNAMIC SCORING
        // ========================================================================

        let revenues = [];
        let spending = [];
        let trustFund = [];
        let federalDebt = [];
        let rateAdjustmentHistory = [];
        let cumulativeGDPMultiplier = 1.0; // Starts at 1.0 (Baseline GDP)
        let cumulativeEventGDPFactor = 1.0; // Tracks ONLY event impacts (for Baseline Shock)
        let cumulativeDebtReduction = 0; // Track cumulative savings ratio
        let activeEventsLog = [];

        // console.log('[runSimulationPass] Starting simulation. Events:', enabledEventIds);

        // PRE-CALCULATE SPENDING for all years (Sec 206 requires "Next Year" outlook)
        // Note: Spending is dollar-denominated based on demographics/unit costs, not direct GDP function.
        // We calculate this preliminarily using baseConfig, but accurate yearly spending requires dynamic events.
        // For the *projection* of next year's outlays (for trust fund), we need a rough estimate.
        // We'll calculate year-by-year inside the loop properly.

        // Ensure simulationYears is set
        const CALCULATION_HORIZON = 21; // Match authoritative geography data window (2026-2046)

        // Ensure simulationYears is set (Default to 10 for display)
        const displayYears = config.simulationYears || 10;
        config.simulationYears = displayYears; // Keep config independent

        // Create a robust year list, extrapolating if needed
        const fullYearData = [];
        const baseYearData = BaselineData.nheBaseline[startIndex]; // 2027

        // Helper: Calculate CAGR
        const calculateCAGR = (start, end, years) => Math.pow(end / start, 1 / years) - 1;

        let trustFundBalance = 0; // Restore missing declaration
        let coreTrustFundBalance = 0;
        let ltssSubAccountBalance = 0;
        let ltssAllocationPct = null; // Set at year 1, used for all years

        for (let i = 0; i < CALCULATION_HORIZON; i++) {
            const targetYear = config.startYear + i;
            // Try to find actual data
            let yearData = BaselineData.nheBaseline.find(d => d.year === targetYear);

            // If missing (e.g. cached data is short), extrapolating from last available
            if (!yearData) {
                const lastKnown = fullYearData.length > 0
                    ? fullYearData[fullYearData.length - 1]
                    : (BaselineData.nheBaseline[BaselineData.nheBaseline.length - 1] || baseYearData);

                yearData = {
                    year: targetYear,
                    // 4% GDP growth, 5% Spending growth (modest extrapolation)
                    gdp: lastKnown.gdp * 1.04,
                    totalHealthSpending: lastKnown.totalHealthSpending * 1.05,
                    population: Math.round(lastKnown.population * 1.005),
                    healthShareGDP: (lastKnown.totalHealthSpending * 1.05) / (lastKnown.gdp * 1.04),
                    // Carry forward shares
                    hospitalShare: lastKnown.hospitalShare,
                    physicianShare: lastKnown.physicianShare,
                    drugShare: lastKnown.drugShare,
                    adminShare: lastKnown.adminShare
                };
            }
            fullYearData.push(yearData);
        }

        // --- NEW: EXTRAPOLATE MED SCHOOL DEBT ---
        // The raw JSON flatlines after 2039. We need to dynamicly extend it.
        const syntheticMedSchoolDebt = [];
        const validDebtYears = BaselineData.medSchoolDebt.filter(d => d.year <= 2039 && d.year >= 2024);

        // Push valid years
        syntheticMedSchoolDebt.push(...validDebtYears);

        // Calculate Trend from last 5 years (2035-2039)
        const trendStart = validDebtYears.find(d => d.year === 2035);
        const trendEnd = validDebtYears.find(d => d.year === 2039);

        let debtCAGR = 0.03; // Default 3%
        let gradCAGR = 0.01; // Default 1%

        if (trendStart && trendEnd) {
            debtCAGR = calculateCAGR(trendStart.avgDebt, trendEnd.avgDebt, 4);
            gradCAGR = calculateCAGR(trendStart.newGraduates, trendEnd.newGraduates, 4);
        }

        // Extrapolate from 2040 to End of Horizon
        const lastValid = validDebtYears[validDebtYears.length - 1];
        let currentDebt = lastValid.avgDebt;
        let currentGrads = lastValid.newGraduates;

        for (let year = 2040; year <= (config.startYear + CALCULATION_HORIZON); year++) {
            currentDebt *= (1 + debtCAGR);
            currentGrads *= (1 + gradCAGR);

            syntheticMedSchoolDebt.push({
                year,
                avgDebt: currentDebt,
                newGraduates: Math.round(currentGrads)
            });
        }

        // Create Synthetic Baseline Data to pass to calculators
        const syntheticBaselineData = {
            ...BaselineData,
            nheBaseline: fullYearData, // Ensure this covers full range
            medSchoolDebt: syntheticMedSchoolDebt
        };

        const debtProjectionsMap = new Map();
        const yearGdpMultipliers = new Map(); // Store annual multipliers

        // Iterate year by year using the robust list
        for (let i = 0; i < fullYearData.length; i++) {
            const yearData = fullYearData[i];
            const year = yearData.year;
            // console.log(`[runSimulationPass] Processing Year ${year}`);

            // 0. Apply Dynamic Events - Config Adjustments
            // This generates a year-specific config with shocks applied
            const { effectiveConfig, activeEvents } = applyEventsToConfig(year, config, enabledEventIds);

            // 0b. Get Event Multipliers (New System)
            const eventMultipliers = getEventMultipliers(year, effectiveConfig, enabledEventIds);

            if (activeEvents.length > 0) {
                activeEventsLog.push({ year, events: activeEvents });
            }

            // 1. Dynamic GDP Adjustment for this year
            // Multiplier impacts GDP growth rate or absolute level?
            // Model: Multipliers are relative to baseline.
            // gdp_growth impact is separate additive factor
            const growthImpact = eventMultipliers.gdp_growth || 0;

            // Apply cumulative growth impact? Or strictly one-off?
            // Usually growth shocks compound.
            // We'll treat 'gdp_growth' as a delta to the growth rate for this year.
            // But here we are just scaling the baseline GDP.
            // Simplified: accumulated multiplier += growth_impact
            cumulativeGDPMultiplier += growthImpact;
            cumulativeEventGDPFactor += growthImpact;

            const gdpMultiplier = cumulativeGDPMultiplier;
            yearGdpMultipliers.set(year, gdpMultiplier); // Store for reporting

            const currentGDP = yearData.gdp * gdpMultiplier;
            // console.log(`[RunScenario] Year ${year} GDP Multiplier: ${gdpMultiplier}, Growth Impact: ${growthImpact}, GDP: ${(currentGDP/1e12).toFixed(2)}T`);

            // Calculate Shocked Baseline GDP (Status Quo + Pandemic)
            const shockedBaselineGDP = yearData.gdp * cumulativeEventGDPFactor;

            // 2. Calculate Spending for this year using EFFECTIVE config AND MULTIPLIERS
            let yearSpending = calculateYearSpending(year, syntheticBaselineData, effectiveConfig, currentGDP, eventMultipliers, structuralOverrides);
            if (!yearSpending) continue;

            // 2b. Pipeline override: use projection_timeline when valid
            let spendingSource = 'simulator';
            if (projectionTimeline && isPipelineValidForYear(projectionTimeline, year)) {
                const pipelineEntry = getPipelineEntryForYear(projectionTimeline, year);
                yearSpending = applyPipelineSpending(yearSpending, pipelineEntry, {
                    ltssMultiplier: effectiveConfig.ltssMultiplier ?? 1.0
                });
                yearSpending.spendingSource = 'pipeline';
                spendingSource = 'pipeline';
            }
            spending.push(yearSpending);

            // 3. Get/Estimate NEXT YEAR's spending for Reserve Ratio
            // We can't know next year's exact spending yet (circular dep on future events), 
            // so we estimate based on current year + 5% trend
            const projectedNextYearOutlays = yearSpending.totalProgramCost * 1.05;

            // Acute vs LTSS outlays (§201(d) split)
            const acuteOutflows = yearSpending.totalProgramCost - (yearSpending.ltss || 0);
            const ltssOutflows = yearSpending.ltss || 0;
            const pipelineEntry = projectionTimeline ? getPipelineEntryForYear(projectionTimeline, year) : null;
            const ltssFields = getPipelineLtssFields(pipelineEntry);
            const ltssCopayDollars = (ltssFields.ltssCopayReceiptsBillions || 0) * 1e9;
            const ltssMiDollars = (ltssFields.ltssMiContribBillions || 0) * 1e9;

            // 4. Calculate Enrollment Rate
            const enrollmentRate = yearSpending.enrollmentRate;

            // 5. Calculate Revenue with CURRENT RATES & DYNAMIC GDP
            // We pass gdpMultiplier to scale the tax base
            let currentRates = {
                employerRate: effectiveConfig.employerRate,
                baseIndividualRate: effectiveConfig.baseIndividualRate,
                baseSelfEmployedRate: effectiveConfig.baseSelfEmployedRate,
                surtaxThresholds: effectiveConfig.surtaxThresholds || [],
                surtaxRates: effectiveConfig.surtaxRates || []
            };

            // If we have history of rate adjustments, use the latest
            if (rateAdjustmentHistory.length > 0) {
                const lastAdj = rateAdjustmentHistory[rateAdjustmentHistory.length - 1];
                currentRates = lastAdj.newRates;
            }

            const yearRevenue = calculateYearRevenue(
                year,
                syntheticBaselineData, // FIX: Use synthetic baseline
                effectiveConfig,
                currentRates,
                enrollmentRate,
                gdpMultiplier,
                yearSpending.totalProgramCost, // Passed for Federal Redirect calculation
                eventMultipliers // Pass for revenue shocks (covid etc)
            );
            if (yearRevenue) revenues.push(yearRevenue);

            // 6. Trust Fund Accounting
            if (yearRevenue) {
                // Total revenue (includes taxes, federal/state redirects, copays)
                // Calculated definitively in revenueCalculator.js

                // --- NEW: TRUST FUND INTEREST INCOME (Sec 205 Investment of Surplus) ---
                // The Trust Fund holds special-issue Treasuries.
                // We assume a blended interest rate. CBO projects ~3.0-3.8% nominal rates for 10Y Treasuries in late 2020s.
                // We'll use a conservative 3.0% effective yield on the *opening* balance for simplicity.
                const interestRate = 0.03;
                const interestIncome = trustFundBalance * interestRate;

                // Add interest to total revenue
                yearRevenue.interestIncome = interestIncome;
                yearRevenue.totalRevenue += interestIncome;

                const totalTrustFundIncomeBeforeTransfer = yearRevenue.totalRevenue;
                const outflows = yearSpending.totalProgramCost;

                // General Fund Transfer (Sec 207)
                const deficit = Math.max(0, outflows - totalTrustFundIncomeBeforeTransfer);
                const yearsActive = year - config.startYear + 1;
                const generalFundLimit = getGeneralFundLimitForYearsActive(yearsActive);
                const maxGeneralFundTransfer = outflows * generalFundLimit;
                let actualGeneralFundTransfer = Math.min(deficit, maxGeneralFundTransfer);

                // Surplus / Excess Reserves Check
                const tentativeInflows = totalTrustFundIncomeBeforeTransfer + actualGeneralFundTransfer;
                const tentativeBalance = trustFundBalance + tentativeInflows - outflows;
                const tentativeReserveRatio = projectedNextYearOutlays > 0 ? (tentativeBalance / projectedNextYearOutlays) : 0;

                // 1. HARD CAP CHECK (New User Slider)
                // If reserve ratio exceeds the user-defined Cap (default 150%), we reduce the Federal Redirect (Base Contribution).
                // This simulates Congress cutting back appropriations because the fund is "too rich".
                const reserveCap = config.reserveFundCap || 1.5; // Default 150%
                let fedRedirectDeduction = 0;

                if (tentativeReserveRatio > reserveCap) {
                    const targetCapBalance = projectedNextYearOutlays * reserveCap;
                    const excessOverCap = tentativeBalance - targetCapBalance;

                    // We can only deduct up to the amount of federal redirect available
                    const availableFedRedirect = yearRevenue.revenueFederalRedirect;
                    fedRedirectDeduction = Math.min(excessOverCap, availableFedRedirect);

                    // Apply deduction
                    yearRevenue.revenueFederalRedirect -= fedRedirectDeduction;

                    // Update derived totals
                    yearRevenue.totalRevenue -= fedRedirectDeduction;
                }

                // Recalculate Totals after potential Federal Deduction
                const totalTrustFundIncomeAfterCap = yearRevenue.totalRevenue; // Updated

                // Recalculate Deficit/GF Transfer needs based on NEW income
                const deficitPostCap = Math.max(0, outflows - totalTrustFundIncomeAfterCap);
                let actualGeneralFundTransferPostCap = Math.min(deficitPostCap, maxGeneralFundTransfer);

                // 2. SOFT CAP CHECK (Bill Statutory Limit - Sec 206)
                // If reserves still exceed statutory max (10%) AFTER the Hard Cap check (unlikely if Hard Cap < 10%, but possible if Cap > 10%)
                // We reduce the General Fund Transfer (if any).

                const tentativeInflows2 = totalTrustFundIncomeAfterCap + actualGeneralFundTransferPostCap;
                const tentativeBalance2 = trustFundBalance + tentativeInflows2 - outflows;
                const excessReserveThreshold = SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMax;

                if (tentativeBalance2 / projectedNextYearOutlays > excessReserveThreshold) {
                    const targetBalance = projectedNextYearOutlays * excessReserveThreshold;
                    const excessFunds = tentativeBalance2 - targetBalance;
                    const transferReduction = Math.min(actualGeneralFundTransferPostCap, excessFunds);
                    actualGeneralFundTransferPostCap -= transferReduction;
                }

                // Finalize variables for rest of loop
                actualGeneralFundTransfer = actualGeneralFundTransferPostCap;
                const totalTrustFundIncomeFinal = totalTrustFundIncomeAfterCap;

                // Final Balance
                const finalInflows = totalTrustFundIncomeFinal + actualGeneralFundTransfer;
                const netChange = finalInflows - outflows;
                const endingBalance = trustFundBalance + netChange;

                // §201(d) LTSS Sub-Account: split CORE vs LTSS when we have LTSS data
                const useLtssSplit = ltssOutflows > 0 || ltssCopayDollars + ltssMiDollars > 0;
                if (useLtssSplit && finalInflows > 0) {
                    if (ltssAllocationPct == null) {
                        ltssAllocationPct = ltssOutflows / outflows || 0;
                    }
                    const coreInflows = (1 - ltssAllocationPct) * finalInflows;
                    const ltssInflowsFromAllocation = ltssAllocationPct * finalInflows;
                    const ltssInflowsTotal = ltssInflowsFromAllocation + ltssCopayDollars + ltssMiDollars;

                    coreTrustFundBalance += coreInflows - acuteOutflows;
                    ltssSubAccountBalance += ltssInflowsTotal - ltssOutflows;

                    trustFundBalance = coreTrustFundBalance + ltssSubAccountBalance;
                } else {
                    coreTrustFundBalance = endingBalance;
                    ltssSubAccountBalance = 0;
                    trustFundBalance = endingBalance;
                }

                // §206 Reserve ratio: CORE only (exclude LTSS copays and MI from receipt side)
                const projectedNextYearCoreOutlays = acuteOutflows * 1.05;
                const reserveRatio = useLtssSplit && projectedNextYearCoreOutlays > 0
                    ? coreTrustFundBalance / projectedNextYearCoreOutlays
                    : (projectedNextYearOutlays > 0 ? endingBalance / projectedNextYearOutlays : 0);
                const isHealthy = reserveRatio >= SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMin
                    && reserveRatio <= SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMax;

                const trustEntry = {
                    year,
                    inflows: finalInflows,
                    outflows,
                    generalFundTransfer: actualGeneralFundTransfer,
                    redirectedSpending: yearRevenue.redirectedFederalSpending,
                    netChange,
                    endingBalance: trustFundBalance,
                    reserveRatio,
                    isHealthy,
                    ratesApplied: { ...currentRates },
                    coreBalance: useLtssSplit ? coreTrustFundBalance : endingBalance,
                    ltssSubAccountBalance: useLtssSplit ? ltssSubAccountBalance : 0,
                    ltssCopayReceipts: ltssCopayDollars,
                    ltssMiContrib: ltssMiDollars
                };
                trustFund.push(trustEntry);

                // 7. Federal Debt Calculation (Dynamic)
                // Use effectiveConfig for debt calculation
                // 7. Federal Debt Calculation (Dynamic)
                // Use effectiveConfig for debt calculation
                const debtImpact = calculateFederalDebtImpact(year, syntheticBaselineData, [yearSpending], [yearRevenue], [trustEntry], effectiveConfig, gdpMultiplier);

                if (debtImpact) {
                    // STOCK-FLOW FIX: Track absolute savings ($), not sum of %
                    // Previous flawed math: cumulativeDebtReduction += debtImpact.deficitReduction (Summing % of differing GDPs)
                    // limit: cumulativeDebtReduction is defined in outer scope, rename or reuse?
                    // It was defined as: let cumulativeDebtReduction = 0; // % of GDP points
                    // We need a NEW variable for dollars: let cumulativeFedSavingsDollars = 0;
                    // I will need to have initialized this outside the loop. 
                    // Since I cannot easily change the initialization in this tool call, I will assume I can't access a new var unless I use an existing one or hack it.
                    // Wait, I can't easily add a variable to the top of the function in a contiguous edit if I'm only editing lines 410-430.

                    // Workaround: Use the existing cumulativeDebtReduction variable, but store DOLLARS in it?
                    // No, that's messy if it was initialized as 0.
                    // Actually, I can use a property on the `debtProjectionsMap` or similar? 
                    // Or I can calculate it on the fly by summing previous years? 
                    // No, that's O(N^2).

                    // BETTER: I will calculate it by summing `federalSavings` from `federalDebt` array which is available in scope!
                    // federalDebt is the array of previous years.
                    // const previousSavings = federalDebt.reduce((sum, d) => sum + d.federalSavings, 0);
                    // cumulativeSavings = previousSavings + debtImpact.federalSavings;

                    const previousSavings = federalDebt.reduce((sum, d) => sum + d.federalSavings * debtReductionReflow, 0);
                    const cumulativeSavingsDollars = previousSavings + debtImpact.federalSavings * debtReductionReflow;

                    // Get current nominal GDP (Shocked/Dynamic)
                    const currentNominalGDP = yearData.gdp * gdpMultiplier;

                    // Calculate Ratio Reduction correctly
                    const ratioReduction = cumulativeSavingsDollars / currentNominalGDP;

                    debtImpact.safecareDebtToGDP = debtImpact.baselineDebtToGDP - ratioReduction;

                    // Store for next iteration (though we recalculate from array)
                    // Update the "cumulativeDebtReduction" var just in case it's used elsewhere (it's not, strictly local loop var)
                    cumulativeDebtReduction = ratioReduction;
                    debtImpact.cumulativeDebtReduction = ratioReduction;

                    // NEW: Calculate Shocked Baseline Debt Ratio
                    // Approximate: Baseline Nominal Debt / Shocked GDP
                    const originalBaselineNominalDebt = debtImpact.baselineDebtToGDP * yearData.gdp;
                    debtImpact.shockedBaselineDebtToGDP = originalBaselineNominalDebt / shockedBaselineGDP;

                    federalDebt.push(debtImpact);
                    debtProjectionsMap.set(year, debtImpact);
                }

                // 8. Dynamic Scoring / Growth Feedback
                const transientResult = {
                    year,
                    safecareHealthShareGDP: (yearSpending.totalProgramCost) / currentGDP,
                    baselineHealthShareGDP: yearData.healthShareGDP,
                    safecareDebtToGDP: debtImpact ? debtImpact.safecareDebtToGDP : yearData.baselineDebtToGDP,
                    baselineDebtToGDP: yearData.baselineDebtToGDP
                };

                const boost = calculateAnnualGrowthBoost(transientResult, effectiveConfig);
                const sensitivity = (effectiveConfig.macroFeedbackSensitivity !== undefined) ? effectiveConfig.macroFeedbackSensitivity : 0.0;

                // Feedback: Add boost to growth rate
                cumulativeGDPMultiplier += (boost * sensitivity);

                // 9. Automatic Rate Adjustments (Sec 206 & 207)
                if (effectiveConfig.enforceTrustFundRules) {
                    // DEBUG: Check for NaN in rates
                    if (Number.isNaN(Number(currentRates.baseIndividualRate))) {
                        console.error(`[runScenario] NaN Rate Detected in ${year}. Trace:`, currentRates);
                    }
                    let adjustmentDirection = null;
                    let adjustmentReason = '';

                    // Sec 207(c) GF Limit Detection
                    const gfLimit = getGeneralFundLimitForYearsActive(yearsActive);

                    const actualOutlays = yearSpending.totalProgramCost;
                    const revenueShortfall = Math.max(0, actualOutlays - yearRevenue.totalRevenue);
                    const gfTransferRatio = revenueShortfall / actualOutlays;

                    // Trigger 1: Trust Fund Reserve Ratio (Sec 206)
                    if (reserveRatio < SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMin) {
                        adjustmentDirection = 'increase';
                        adjustmentReason = `Reserve ratio ${(reserveRatio * 100).toFixed(1)}% < 5% (Sec 206)`;
                    } else if (reserveRatio > SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMax) {
                        adjustmentDirection = 'decrease';
                        adjustmentReason = `Reserve ratio ${(reserveRatio * 100).toFixed(1)}% > 10% (Sec 206)`;
                    }

                    // Trigger 2: General Fund Reliance (Sec 207(f)(3) - Dedicated Funding Convergence)
                    // Only triggers increase if not already increasing and GF limit is exceeded
                    if (gfTransferRatio > gfLimit && adjustmentDirection !== 'increase') {
                        adjustmentDirection = 'increase';
                        adjustmentReason = `GF Transfer ${(gfTransferRatio * 100).toFixed(1)}% > ${(gfLimit * 100).toFixed(1)}% Limit (Sec 207)`;
                    }

                    if (adjustmentDirection && effectiveConfig.enableAutomaticAdjustments !== false) {
                        // Sec 206(i) INITIAL STABILIZATION LOCK
                        // "Notwithstanding subsection (d)... no increase in any rate... may take effect... [for 3 years]"
                        // Note: Downward adjustments (Sec 206(e)) ARE permitted during lock period (Sec 206(i)(3))
                        if (adjustmentDirection === 'increase' && yearsActive <= 3) {
                            adjustmentDirection = null;
                        }
                    }

                    // Political-economy stress tests:
                    // Allow users to model Congressional override / non-enforcement of automatic steps.
                    if (adjustmentDirection === 'increase' && effectiveConfig.allowAutoIncrease === false) {
                        adjustmentDirection = null;
                    }
                    if (adjustmentDirection === 'decrease' && effectiveConfig.allowAutoDecrease === false) {
                        adjustmentDirection = null;
                    }

                    if (adjustmentDirection && effectiveConfig.enableAutomaticAdjustments !== false) {
                        const autoAdjConfig = SAFECARE_BILL_PARAMETERS.trustFund.autoAdjustment;
                        const sign = adjustmentDirection === 'increase' ? 1 : -1;

                        // Apply increments with statutory caps (Sec 206(f))
                        // One step per year fits within the annual caps (0.6, 0.3, 0.9)

                        // FIX: Ensure values are numbers before adding
                        const empRate = Number(currentRates.employerRate) || 0;
                        const indRate = Number(currentRates.baseIndividualRate) || 0;
                        const seRate = Number(currentRates.baseSelfEmployedRate) || 0;

                        currentRates.employerRate = Math.max(SAFECARE_BILL_PARAMETERS.trustFund.minRate,
                            Math.min(SAFECARE_BILL_PARAMETERS.trustFund.maxRate, empRate + autoAdjConfig.employerStep * sign));

                        currentRates.baseIndividualRate = Math.max(SAFECARE_BILL_PARAMETERS.trustFund.minRate,
                            Math.min(SAFECARE_BILL_PARAMETERS.trustFund.maxRate, indRate + autoAdjConfig.individualStep * sign));

                        currentRates.baseSelfEmployedRate = Math.max(SAFECARE_BILL_PARAMETERS.trustFund.minRate,
                            Math.min(SAFECARE_BILL_PARAMETERS.trustFund.maxRate, seRate + autoAdjConfig.selfEmployedStep * sign));

                        // Surtax adjustments (Sec 206(d)(4))
                        if (currentRates.surtaxRates && currentRates.surtaxRates.length > 0) {
                            currentRates.surtaxRates = currentRates.surtaxRates.map(r =>
                                Math.max(0, (Number(r) || 0) + autoAdjConfig.surtaxStep * sign)
                            );
                        }

                        rateAdjustmentHistory.push({
                            year,
                            reason: adjustmentReason,
                            direction: adjustmentDirection,
                            newRates: JSON.parse(JSON.stringify(currentRates)) // Deep copy to persist
                        });
                    }
                }
            }
        }


        // console.log(`[runSimulationPass] Simulation complete. Final GDP Multiplier: ${cumulativeGDPMultiplier.toFixed(4)}`);

        // ========================================================================
        // POST-SIMULATION ANALYSIS
        // ========================================================================
        // Now that we have the full trajectory of spending and revenues (including dynamic adjustments),
        // we run the Trust Fund Projection through the enhanced calculator to get granular savings breakdowns.

        let trustFundSummary = {};
        let richTrustData = null;

        try {
            // Use fullYearData (which includes extrapolations) instead of raw BaselineData
            // This ensures the projection corresponds exactly to the simulation loop years
            // FIX: Pass trustFund array to preserve rate data
            const configWithTrustData = { ...config, trustFundInput: trustFund };
            richTrustData = calculateTrustFundProjection({ nheBaseline: fullYearData }, revenues, spending, configWithTrustData);

            // Overwrite with rich analytical data, but preserve CORE/LTSS fields when present (§201(d))
            if (richTrustData && richTrustData.yearlyData) {
                const hasLtssSplit = trustFund.some(t => t.coreBalance != null || t.ltssSubAccountBalance != null);
                if (hasLtssSplit) {
                    // Merge: use rich data but preserve our §206 CORE reserve ratio and LTSS fields
                    trustFund = richTrustData.yearlyData.map((rich, i) => {
                        const ours = trustFund.find(t => t.year === rich.year);
                        return ours ? { ...rich, reserveRatio: ours.reserveRatio, coreBalance: ours.coreBalance, ltssSubAccountBalance: ours.ltssSubAccountBalance, ltssCopayReceipts: ours.ltssCopayReceipts, ltssMiContrib: ours.ltssMiContrib, isHealthy: ours.isHealthy } : rich;
                    });
                } else {
                    trustFund = richTrustData.yearlyData;
                }
                trustFundSummary = richTrustData.summary;
            }
        } catch (e) {
            console.error('[runSimulationPass] Trust Fund Projection Failed:', e);
            // Fallback: trustFund array remains what was generated in the loop
        }

        // 4. Calculate household burden
        const householdBurden = calculateHouseholdBurdenByDecile(syntheticBaselineData, config);
        const householdSummary = calculateAggregateHouseholdSavings(householdBurden);

        // 5. Calculate debt impacts
        const debtProjections = calculateDebtProjections(syntheticBaselineData, config);
        const workforceImpact = calculateWorkforceDebtImpact(debtProjections);

        // 6. Combine into yearly results for charts
        // Use fullYearData which matches the simulation loop range
        let yearlyResults = fullYearData.map(yearData => {
            const year = yearData.year;
            const rev = revenues.find(r => r.year === year);
            const spend = spending.find(s => s.year === year);
            const trust = trustFund.find(t => t.year === year);
            const debt = federalDebt.find(d => d.year === year);
            const medDebt = debtProjections.medicalDebt.find(d => d.year === year);
            const medSchoolDebt = debtProjections.medSchoolDebt.find(d => d.year === year);
            const eventsForYear = activeEventsLog.find(l => l.year === year)?.events || [];

            return {
                year,
                // Health spending as % of GDP (Baseline Denominator for comparability)
                // If we have data showing the baseline is shocked (via debt ratio), we apply that shock to the baseline line too.
                baselineHealthShareGDP: (() => {
                    // 1. Check if we have a GDP shock (via debt object) or if events were active
                    // FIX: Use tolerance for floating point comparison to avoid false positives
                    if ((debt && debt.shockedBaselineDebtToGDP && Math.abs(debt.shockedBaselineDebtToGDP - debt.baselineDebtToGDP) > 1e-9) || eventsForYear.length > 0) {
                        // Re-calculate Event Multipliers for this year to get Spending Shock
                        // Re-calculate Event Multipliers for this year to get Spending Shock
                        // FIX: Pass 'enabledEventIds' (the full state object) to preserve Trigger Year
                        const { effectiveConfig: yearConfig } = applyEventsToConfig(year, config, enabledEventIds);
                        const multipliers = getEventMultipliers(year, yearConfig, enabledEventIds);

                        // GDP Shock Factor (Shocked / Base)
                        const gdpShockFactor = (debt && debt.shockedBaselineDebtToGDP)
                            ? (debt.baselineDebtToGDP / debt.shockedBaselineDebtToGDP)
                            : (1.0 + (multipliers.gdp_growth || 0));

                        // Spending Shock (Numerator)
                        // Weighted average of component shocks from actual Baseline Data segments
                        const sHosp = yearData.hospitalShare || 0.33;
                        const sPhys = yearData.physicianShare || 0.20;
                        const sDrug = yearData.drugShare || 0.10;
                        const sAdmin = yearData.adminShare || 0.07;
                        // Ensure shares don't exceed 1.0 (though they should sum to 1.0)
                        const sOther = Math.max(0, 1.0 - (sHosp + sPhys + sDrug + sAdmin));

                        const mHosp = multipliers.hospital || 1.0;
                        const mPhys = multipliers.physician || 1.0;
                        const mDrug = multipliers.drugs || 1.0;
                        const mAdmin = multipliers.admin || 1.0;

                        // Calculate aggregate multiplier
                        const spendingShock = (sHosp * mHosp) + (sPhys * mPhys) + (sDrug * mDrug) + (sAdmin * mAdmin) + (sOther * 1.0);

                        return (yearData.healthShareGDP * spendingShock) / gdpShockFactor;
                    }
                    return yearData.healthShareGDP;
                })(),
                safecareHealthShareGDP: spend ? (spend.totalProgramCost) / (yearData.gdp * (yearGdpMultipliers.get(year) || 1)) : yearData.healthShareGDP,

                // Federal debt
                baselineDebtToGDP: debt ? (debt.shockedBaselineDebtToGDP || debt.baselineDebtToGDP) : (federalDebt[0]?.baselineDebtToGDP || null),
                safecareDebtToGDP: debt ? debt.safecareDebtToGDP : (federalDebt[0]?.baselineDebtToGDP || null),

                // Drug spending
                baselineDrugSpending: yearData.totalHealthSpending * yearData.drugShare,
                safecareDrugSpending: spend ? (spend.drug || 0) : (yearData.totalHealthSpending * yearData.drugShare),

                // Admin spending
                baselineAdminSpending: yearData.totalHealthSpending * yearData.adminShare,
                safecareAdminSpending: spend ? (spend.admin || 0) : (yearData.totalHealthSpending * yearData.adminShare),

                // Medical debt
                medicalDebtBaseline: medDebt ? medDebt.baselineDebt : null,
                medicalDebtSafecare: medDebt ? medDebt.remainingDebt : (medDebt?.baselineDebt || null),

                // Med school debt
                medSchoolDebtBaseline: medSchoolDebt ? medSchoolDebt.baselineDebt : null,
                medSchoolDebtSafecare: medSchoolDebt ? medSchoolDebt.safecareDebt : (medSchoolDebt?.baselineDebt || null),

                // Trust Fund
                trustFundBalance: trust ? trust.endingBalance : 0,
                trustFundReserveRatio: trust ? trust.reserveRatio : 0,
                trustFundHealthy: trust ? trust.isHealthy : true,
                trustFundGeneralFundTransfer: trust ? trust.generalFundTransfer : 0,
                coreBalance: trust?.coreBalance,
                ltssSubAccountBalance: trust?.ltssSubAccountBalance,

                // Revenues and spending
                totalRevenue: trust ? trust.inflows : 0,
                totalSpending: spend ? (spend.totalProgramCost || 0) : 0,
                // Reference: total NHE (all payers) - for context only; not comparable to program cost
                baselineTotalNHE: yearData.totalHealthSpending,
                // Comparison: baseline federal health outlays (CBO) - apples-to-apples with SAFECare program
                baselineGovtHealthSpending: debt ? debt.baselineFederalHealth : null,
                baselineTotalSpending: yearData.totalHealthSpending, // alias for backward compat
                // Govt-to-govt: baseline federal health outlays (CBO) vs SAFECare program cost
                totalSavings: spend && debt ? ((debt.baselineFederalHealth || 0) - (spend.totalProgramCost || 0)) : 0,

                // Breakdown
                revenueEmployer: rev ? rev.employerRevenue : 0,
                revenueIndividual: rev ? rev.individualRevenue : 0,
                revenueSelfEmployed: rev ? rev.selfEmployedRevenue : 0,
                revenueInterest: rev ? (rev.interestIncome || 0) : 0, // NEW
                revenueFederalRedirect: rev ? rev.revenueFederalRedirect : 0,
                revenueStateRedirect: rev ? rev.revenueStateRedirect : 0,
                revenueGenFund: trust ? ((trust.actualGeneralFundTransfer || 0) - (trust.generalFundRepayment || 0)) : 0, // Net Gen Fund (Borrowing - Repayment)
                generalFundDebt: trust ? (trust.accumulatedGeneralFundDebt || 0) : 0, // Track Debt Accumulation
                revenueCopay: rev ? rev.copaymentRevenue : 0,
                // Critical for diagnostics
                integrationRate: rev ? rev.integrationRate : 0,

                costProvider: spend ? ((spend.provider || 0) + (spend.otherMedical || 0)) : 0,
                costDrug: spend ? (spend.drug || 0) : 0,
                costAdmin: spend ? (spend.admin || 0) : 0,
                costExtraBehavioral: spend ? (spend.extraBehavioralCosts || 0) : 0,

                // Explicit New Benefits Total
                costNewBenefits: spend ? ((spend.dental || 0) + (spend.vision || 0) + (spend.ltss || 0)) : 0,

                // Detailed Program Costs (Fixed Missing Fields)
                costTuitionSupport: spend ? (spend.tuitionSupport || 0) : 0,
                costLoanForgiveness: spend ? (spend.loanForgiveness || 0) : 0,
                // Accessing loanForgiveness as costTransition for table compatibility if needed, 
                // but better to export explicit fields.
                costTransition: spend ? (spend.loanForgiveness || 0) : 0, // Mapping loan forgiveness here for now

                costDebtRedemption: spend ? (spend.debtRedemption || 0) : 0,
                costSafetyNet: spend ? (spend.safetyNet || 0) : 0,
                costCapitalGrants: spend ? (spend.capitalGrants || 0) : 0,
                costInnovatorSupport: spend ? (spend.smallInnovatorSupport || 0) : 0,

                costDental: spend ? (spend.dental || 0) : 0,
                costVision: spend ? (spend.vision || 0) : 0,
                costLTSS: spend ? (spend.ltss || 0) : 0,
                ltssOutlaysHcbsBillions: spend?.ltssOutlaysHcbsBillions,
                ltssOutlaysNfBillions: spend?.ltssOutlaysNfBillions,
                ltssCopayReceiptsBillions: spend?.ltssCopayReceiptsBillions ?? (trust?.ltssCopayReceipts != null ? trust.ltssCopayReceipts / 1e9 : null),
                ltssMiContribBillions: spend?.ltssMiContribBillions ?? (trust?.ltssMiContrib != null ? trust.ltssMiContrib / 1e9 : null),

                // Data provenance
                spendingSource: spend?.spendingSource || 'simulator',

                // Events
                activeEvents: eventsForYear.map(e => e.label),

                // Rate Information
                ratesApplied: trust?.ratesApplied || rev?.ratesApplied || {
                    employerRate: config.employerRate,
                    baseIndividualRate: config.baseIndividualRate,
                    baseSelfEmployedRate: config.baseSelfEmployedRate
                },
                rateAdjustmentTrigger: rateAdjustmentHistory.find(adj => adj.year === year) || null
            };
        });

        // 7. Calculate macroeconomic impacts
        const macroImpacts = calculateMacroImpacts(yearlyResults, config);

        // 8. Calculate success gauge
        const successGauge = calculateSuccessGauge({
            yearlyResults,
            householdBurden,
            workforceImpact,
            macroImpacts,
            config
        });

        // FORCE SLICE: Ensure we never return more years than requested
        // FIX: Removed slicing to always return full calculation horizon.
        // The UI will handle display range.
        /* 
        const requestedYears = config.simulationYears || 13;
        if (yearlyResults.length > requestedYears) {
            yearlyResults = yearlyResults.slice(0, requestedYears);
        }
        */

        const activeYears = yearlyResults.filter(y => y.year >= config.startYear);

        // Filter activeYears for Summary Calculation based on Display Request
        // We use Math.min to handle cases where simulationYears might imply more than CALCULATION_HORIZON (unlikely but safe)
        const displayCount = Math.min(activeYears.length, config.simulationYears || 10);
        const displayYearsSubset = activeYears.slice(0, displayCount);

        const firstActiveYear = displayYearsSubset[0] || yearlyResults[0];
        const lastActiveYear = displayYearsSubset[displayYearsSubset.length - 1];

        return {
            config,
            yearlyResults, // Return FULL dataset (34 years) for advanced use/charts slicing
            householdBurden,
            householdSummary,
            workforceImpact,
            macroImpacts,
            successGauge,
            rateAdjustmentHistory,
            activeEventsLog,
            trustFund: richTrustData, // Full rich data for diagnostics
            summary: {
                firstYear: firstActiveYear,
                lastYear: lastActiveYear,
                totalYears: displayYearsSubset.length,
                avgHealthShareGDP: displayYearsSubset.reduce((sum, y) =>
                    sum + (y.safecareHealthShareGDP || 0), 0) / displayYearsSubset.length,
                totalSavings: displayYearsSubset.reduce((sum, y) =>
                    sum + (y.totalSavings || 0), 0),
                averageSurplus: displayYearsSubset.reduce((sum, y) => {
                    const rev = y.totalRevenue || 0;
                    const exp = y.totalSpending || 0;
                    return sum + (rev - exp);
                }, 0) / (displayYearsSubset.length || 1),
                // Enhanced Trust Fund Summary from V3 Calculator
                trustFund: trustFundSummary
            }
        };
    } catch (error) {
        console.error('Error running simulation pass:', error);
        return {
            error: error.message,
            config,
            yearlyResults: [],
            householdBurden: [],
            householdSummary: null,
            workforceImpact: null,
            summary: null
        };
    }
}

/**
 * Get a quick summary of scenario impacts
 */
export function getScenarioSummary(results) {
    if (!results || !results.summary) return null;

    const first = results.summary.firstYear;
    const last = results.summary.lastYear;

    return {
        healthSpendingReduction: {
            firstYear: ((first.baselineHealthShareGDP - first.safecareHealthShareGDP) /
                first.baselineHealthShareGDP * 100).toFixed(1) + '%',
            lastYear: ((last.baselineHealthShareGDP - last.safecareHealthShareGDP) /
                last.baselineHealthShareGDP * 100).toFixed(1) + '%'
        },
        debtReduction: {
            firstYear: ((first.baselineDebtToGDP - first.safecareDebtToGDP) * 100).toFixed(1) + ' pp',
            lastYear: ((last.baselineDebtToGDP - last.safecareDebtToGDP) * 100).toFixed(1) + ' pp'
        },
        householdSavings: {
            avgPerPerson: '$' + Math.round(results.householdSummary?.avgSavingsPerPerson || 0).toLocaleString(),
            totalPercent: (results.householdSummary?.savingsPercent || 0).toFixed(1) + '%'
        },
        medicalDebt: {
            eliminated: '$' + Math.round((first.medicalDebtBaseline - last.medicalDebtSafecare) / 1e9).toLocaleString() + 'B'
        },
        medSchoolDebt: {
            reduction: '$' + Math.round((first.medSchoolDebtBaseline - last.medSchoolDebtSafecare) / 1000).toLocaleString() + 'k per graduate'
        }
    };
}
